import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
// @ts-ignore
import classnames from 'classnames/bind'
import {
  Maybe,
  // @ts-ignore
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import type { Listing, TransactionContextIdentifierInput } from '@adc/sdk/types'
import { StripeError } from '@stripe/stripe-js'
import { contactNumbers } from '@adc/constants'
import { Alert, CardContainer, Text, LabelValue } from '../../elementals'
import { ColorNames } from '../../../constants/colorVars'
import { useStripePayment, useBreakpoints } from '../../../hooks'
import { formatPhoneNumber } from '../../../utils/formatters'
import {
  capturePaymentMethodForRefund,
  createPaymentIntent,
} from '../../../providers/wallet'
import styles from './styles.scss'
import { CardContainerProps, SlottableComponent } from '../../../types'
import { images } from '../../../images/exported'
import {
  BID_DEPOSIT_GUIDELINES_LINK,
  PARTICIPATION_TERMS_LINK,
} from '../../../constants/statics'

const cx = classnames.bind(styles)

interface OnlineStripePaymentFormProps
  extends SlottableComponent<'success' | 'error'> {
  mode?: 'registration' | 'fund' | 'refund'
  contextIdentifier?: TransactionContextIdentifierInput
  listing?: Listing
  customCTALabel?: string
  customSubmitButton?: boolean
  isPaymentDisabled?: boolean
  isSetup?: boolean
  stripeCardProps?: Omit<CardContainerProps, 'children'>
  containerCardProps?: Omit<CardContainerProps, 'children'>
  showAmountDue?: boolean
  showDisclaimer?: boolean
  // Callbacks
  onClickCTA?: () => void
  onConfirmPayment?: () => void
  onPaymentError?: (e?: StripeError) => void
  onPaymentIntentError?: (error: Error) => void
  onRefReady?: () => void
}

export interface OnlineStripePaymentFormRef {
  isProcessing: boolean
  isLoading: boolean
  isDisabled?: boolean
  isSubmitDisabled?: boolean
  isPaymentFulfilled: boolean
  paymentError: Maybe<StripeError>
  paymentIntentError: Maybe<Error>
  handleSubmit: () => void
}

export const OnlineStripePaymentAmountDue = ({
  amountDue,
}: {
  amountDue: number
}) => {
  const breakpoints = useBreakpoints()
  const isMobile = breakpoints === 'mobile'

  return (
    <CardContainer
      backgroundColor={ColorNames.OffWhiteDark}
      borderRadius='medium'
      padding={isMobile ? 'none' : 'medium'}
    >
      <LabelValue
        level={1}
        isMoneyValue
        value={amountDue}
        label='Amount due'
        classNames={{
          label: cx('flex-container', 'center'),
          value: cx('flex-container', 'center'),
        }}
        elmId='bid_deposit_amount_due_label_value'
      />
    </CardContainer>
  )
}

export const OnlineStripePaymentDisclaimer = ({
  customCTALabel = 'Secure & Register',
}: {
  customCTALabel?: string
}) => (
  <Text className={cx('u-mt-4', 'u-mb-1')}>
    In the event you fail to perform in accordance with the{' '}
    <a
      className={cx('u-nowrap', 'link')}
      href={PARTICIPATION_TERMS_LINK}
      data-elm-id='participation_terms_link'
      target='_blank'
      rel='noreferrer'
    >
      Participation Terms
    </a>
    , the full deposit amount shall be paid as liquidated damages and not as a
    penalty. <a href={BID_DEPOSIT_GUIDELINES_LINK}>Click here</a> to learn more
    about deposit policies. By clicking <strong>{customCTALabel}</strong>, you
    agree that you have read and agree to our rules.
  </Text>
)

const OnlineStripePaymentForm = forwardRef<
  OnlineStripePaymentFormRef,
  OnlineStripePaymentFormProps
>((props, ref) => {
  const {
    contextIdentifier,
    isPaymentDisabled,
    listing,
    slots,
    customCTALabel = 'Secure & Register',
    customSubmitButton = false,
    mode = 'fund',
    containerCardProps = {},
    stripeCardProps = {},
    // Callbacks
    onClickCTA,
    onConfirmPayment,
    onPaymentError,
    onPaymentIntentError,
    onRefReady,
    isSetup = false,
    showAmountDue = true,
    showDisclaimer = true,
  } = props
  const [clientSecret, setClientSecret] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [paymentError, setPaymentError] = useState<Maybe<StripeError>>(null)
  const [isPaymentFulfilled, setIsPaymentFulfilled] = useState<boolean>(false)
  const [paymentIntentError, setPaymentIntentError] =
    useState<Maybe<Error>>(null)

  const isRefund = mode === 'refund'

  const { balance_deficit_amount_required } =
    listing?.current_user_bid_deposit_requirements || {}

  const contextId = contextIdentifier?.id
  const contextSource = contextIdentifier?.context_source
  const contextAction = contextIdentifier?.action

  useEffect(() => {
    setIsLoading(true)

    const contextIdentifierInput:
      | TransactionContextIdentifierInput
      | undefined =
      contextId && contextSource
        ? {
            id: contextId,
            context_source: contextSource,
            action: contextAction,
          }
        : undefined

    const paymentIntentPromise = isRefund
      ? capturePaymentMethodForRefund(
          graph.enums.BuyerPaymentType.OnlineBidDeposit,
          contextIdentifierInput
        )
      : createPaymentIntent(
          graph.enums.BuyerPaymentType.OnlineBidDeposit,
          contextIdentifierInput
        )
    paymentIntentPromise
      .then(({ client_secret }) => {
        setClientSecret(client_secret as string)
      })
      .catch((err: Error) => {
        setPaymentIntentError(err)
        onPaymentIntentError?.(err)
      })
      .finally(() => setIsLoading(false))
  }, [contextId, contextSource, contextAction, isRefund, onPaymentIntentError])

  const handleConfirmPayment = useCallback(() => {
    setIsLoading(false)
    setIsPaymentFulfilled(true)
    setPaymentError(null)
    onConfirmPayment?.()
  }, [onConfirmPayment])

  const handlePaymentError = useCallback(
    (error: StripeError) => {
      setIsLoading(false)
      setPaymentError(error)

      // User callback (can be used for analytics)
      onPaymentError?.(error)
    },
    [onPaymentError]
  )

  const handleClickCTA = useCallback(() => {
    // User callback (can be used for analytics)
    onClickCTA?.()
  }, [onClickCTA])

  const isRetryableError =
    paymentError?.type === 'card_error' ||
    paymentError?.type === 'validation_error'

  const shouldShowPaymentSection =
    (!paymentError || isRetryableError) && !paymentIntentError

  const customerServiceLink = useMemo(
    () => (
      <>
        <a href={`tel:${contactNumbers.ONLINE_BUYER_EXPERIENCE}`}>
          {formatPhoneNumber(contactNumbers.ONLINE_BUYER_EXPERIENCE)}
        </a>{' '}
        for assistance.
      </>
    ),
    []
  )

  const errorAlertContent = useMemo(() => {
    if (paymentIntentError) {
      return (
        <>
          <Text elmId='payment_intent_error_message'>
            {mode === 'refund'
              ? 'Unfortunately, your original deposit payment method is no longer able to accept a refund. Please provide a different credit or debit card below and try again.'
              : mode === 'fund'
              ? 'Bid deposit is currently unavailable. Please refresh the page or try again later.'
              : 'Registration deposit is currently unavailable. Please refresh the page or try again later.'}
          </Text>
          <Text elmId='payment_intent_error_contact_message'>
            {mode === 'fund'
              ? `If the issue persists, please contact customer service at `
              : 'If the issue persists, contact customer service at '}
            {customerServiceLink}
          </Text>
        </>
      )
    }
    if (isRetryableError) {
      return (
        <Text elmId='deposit_error_message'>
          {mode === 'registration'
            ? 'You are not yet registered for this auction. Try again or select another payment method.'
            : 'Try again or select another payment method.'}
        </Text>
      )
    }
    return (
      <Text elmId='deposit_error_message'>
        {mode === 'registration' ? (
          <>
            We are unable to complete your Deposit at this time. Click the{' '}
            <strong>Previous</strong> button to start over or contact customer
            service at {customerServiceLink}
          </>
        ) : (
          <>
            We are unable to complete your Online Bid Deposit fund at this time.
            Please contact customer service at {customerServiceLink}
          </>
        )}
      </Text>
    )
  }, [paymentIntentError, isRetryableError, customerServiceLink, mode])

  const successfulAlertContent = useMemo(() => {
    if (mode === 'refund') {
      return (
        <div>
          <Text>
            Your deposit refund request is in progress. Refunds can take up to
            10 business days to process.
          </Text>
          <Text className='u-mt-2'>
            If you want to participate in online transactions that require a
            deposit, simply fund your deposit account again. Thank you for
            investing with Auction.com!
          </Text>
        </div>
      )
    }
    if (mode === 'fund') {
      return <Text>You can manage your deposit in your wallet.</Text>
    }
    return (
      <Text>
        Thank you, your deposit has been received. You are ready to register,
        transact and close!
      </Text>
    )
  }, [mode])

  const {
    StripePaymentSetupForm,
    isProcessing,
    isDisabled,
    isSubmitDisabled,
    handleSubmit,
  } = useStripePayment({
    clientSecret,
    customCTALabel,
    customSubmitButton,
    onClickCTA: handleClickCTA,
    isLoading: isLoading,
    paymentDisabled: isPaymentDisabled || isPaymentFulfilled,
    confirmPayment: handleConfirmPayment,
    // @ts-ignore next
    paymentErrorHandler: handlePaymentError,
    isSetup,
    additionalInfo: showDisclaimer ? (
      <OnlineStripePaymentDisclaimer customCTALabel={customCTALabel} />
    ) : undefined,
  })

  // Expose handleSubmit to parent
  useImperativeHandle(
    ref,
    () => {
      const refObject = {
        isDisabled,
        isLoading,
        isProcessing,
        isSubmitDisabled,
        isPaymentFulfilled,
        paymentError,
        paymentIntentError,
        handleSubmit,
      }
      onRefReady?.()
      return refObject
    },
    [
      isDisabled,
      isLoading,
      isProcessing,
      isSubmitDisabled,
      isPaymentFulfilled,
      paymentError,
      paymentIntentError,
      handleSubmit,
      onRefReady,
    ]
  )

  return (
    <CardContainer
      className={cx('stripe-container')}
      padding='none'
      {...containerCardProps}
    >
      {mode === 'fund' && (
        <Text className='u-mb-2' size='large' color={ColorNames.Gray}>
          Submit your refundable deposit to access all online auctions and help
          keep the marketplace fair and transparent for everyone.
        </Text>
      )}
      {showAmountDue && (
        <OnlineStripePaymentAmountDue
          amountDue={balance_deficit_amount_required ?? 0}
        />
      )}
      <CardContainer padding='none' {...stripeCardProps}>
        <div className={cx('flex-container', 'u-mt-2', 'u-mb-2')}>
          <Text size='huge' color={ColorNames.Gray}>
            Payment
          </Text>
          <img
            src={images.poweredByStripe}
            width={120}
            alt='Powered by Stripe Badge'
          />
        </div>
        {(Boolean(paymentError) || Boolean(paymentIntentError)) && (
          <>
            {slots?.error ? (
              slots.error()
            ) : (
              // @ts-ignore next
              <Alert
                showIcon
                className={cx('alert')}
                iconName='exclamation-circle'
                role='error'
                header={
                  paymentIntentError
                    ? 'System Issue'
                    : `${isRefund ? 'Refund' : 'Deposit'} Unsuccessful`
                }
                content={errorAlertContent}
              />
            )}
          </>
        )}
        {shouldShowPaymentSection &&
          (isPaymentFulfilled ? (
            <>
              {slots?.success ? (
                slots.success()
              ) : (
                <Alert
                  showIcon
                  className={cx('alert')}
                  iconName='check-circle'
                  role='success'
                  header={isRefund ? 'Refund Successful' : 'Deposit Successful'}
                  content={successfulAlertContent}
                />
              )}
            </>
          ) : (
            StripePaymentSetupForm
          ))}
      </CardContainer>
    </CardContainer>
  )
})

export default OnlineStripePaymentForm
